import React, { useState, useEffect, useCallback, useRef } from 'react';
import API from '../api';
import PoolSelector from '../components/PoolSelector';
import UserAvatar from '../components/UserAvatar';

const s = {
  page: { padding: '24px 20px', maxWidth: 760, margin: '0 auto' },
  title: { fontFamily: "'Oswald', sans-serif", fontSize: 28, fontWeight: 700, color: '#fff', marginBottom: 4 },
  sub: { color: '#667', fontSize: 14, marginBottom: 20 },
  card: { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12, padding: 18, marginBottom: 16 },
  sectionTitle: { fontFamily: "'Oswald', sans-serif", fontSize: 13, color: '#7fb3ff', letterSpacing: 1.2, fontWeight: 700, textTransform: 'uppercase', marginBottom: 12 },
  btn: { padding: '8px 16px', borderRadius: 8, border: 'none', fontSize: 13, fontWeight: 700, cursor: 'pointer' },
  btnPrimary: { background: 'linear-gradient(135deg, #013369, #0a5299)', color: '#fff' },
  btnMuted: { background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', color: '#aab' },
  gameRow: { display: 'flex', gap: 8, marginBottom: 8, alignItems: 'stretch' },
  teamBtn: {
    flex: 1, padding: '12px 10px', borderRadius: 8, cursor: 'pointer',
    border: '1px solid rgba(255,255,255,0.12)', background: 'rgba(255,255,255,0.04)',
    color: '#dde', fontSize: 14, fontWeight: 700, textAlign: 'center', transition: 'all 0.15s'
  },
  teamBtnPicked: { background: 'linear-gradient(135deg, #1b7a3a, #0f5226)', borderColor: '#2ecc71', color: '#fff' },
  teamBtnUsed: { opacity: 0.35, cursor: 'not-allowed', textDecoration: 'line-through' },
  teamBtnLocked: { opacity: 0.4, cursor: 'not-allowed' },
  vs: { alignSelf: 'center', color: '#556', fontSize: 11, fontWeight: 700, padding: '0 2px' },
  memberRow: { display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderBottom: '1px solid rgba(255,255,255,0.05)' },
  aliveBadge: { fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 10, background: 'rgba(46,204,113,0.15)', border: '1px solid rgba(46,204,113,0.4)', color: '#2ecc71', textTransform: 'uppercase' },
  outBadge: { fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 10, background: 'rgba(213,10,10,0.13)', border: '1px solid rgba(213,10,10,0.35)', color: '#ff8888', textTransform: 'uppercase' },
  histDot: { display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 11, padding: '2px 6px', borderRadius: 6, marginLeft: 4 }
};

// 'push' is a tie: survived, but not a win. It needs its own look rather than
// falling through to the pending grey — in a contest where the whole question
// is "am I still in", a settled week must not read as one still being played.
function outcomeStyle(outcome) {
  if (outcome === 'win')  return { background: 'rgba(46,204,113,0.13)', color: '#2ecc71' };
  if (outcome === 'loss') return { background: 'rgba(213,10,10,0.13)', color: '#ff8888' };
  if (outcome === 'push') return { background: 'rgba(127,179,255,0.10)', color: '#8fa6c4' };
  return { background: 'rgba(255,255,255,0.05)', color: '#889' };
}

export default function SurvivorPage({ user, pools, selectedPool, onSelectPool, onManagePools }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  // A second paid entry is a second life: its own team each week, its own
  // used-teams list, and it survives its twin going out.
  const [entryId, setEntryId] = useState(null);
  const poolId = selectedPool?.id;

  const reqRef = useRef(0);
  const load = useCallback(async (id) => {
    const ticket = ++reqRef.current;
    if (!poolId) { setData(null); setLoading(false); return; }
    try {
      const res = await API.get(`/survivor/${poolId}` + (id ? `?entry_id=${id}` : ''));
      if (ticket !== reqRef.current) return;   // a newer pool won the race
      setData(res.data);
      setEntryId(res.data?.me?.entry_id ?? null);
    } catch (e) {
      if (ticket === reqRef.current) setData(null);
    } finally {
      if (ticket === reqRef.current) setLoading(false);
    }
  }, [poolId]);

  // Switching pools has to drop the remembered life — it belongs to the old one.
  useEffect(() => { setLoading(true); setEntryId(null); load(null); }, [load]);

  const enableSurvivor = async (enabled) => {
    setBusy(true);
    try {
      await API.put(`/survivor/${poolId}/enable`, { enabled });
      await load(entryId);
    } catch (e) { alert(e.response?.data?.error || 'Failed'); }
    finally { setBusy(false); }
  };

  const pickTeam = async (team) => {
    setBusy(true);
    try {
      await API.post(`/survivor/${poolId}/pick`, { team, entry_id: entryId });
      await load(entryId);
    } catch (e) { alert(e.response?.data?.error || 'Failed'); }
    finally { setBusy(false); }
  };

  const switchLife = async (id) => {
    if (id === entryId) return;
    setBusy(true);
    try { await load(id); } finally { setBusy(false); }
  };

  const body = () => {
    if (loading) return <div style={{ color: '#667', textAlign: 'center', paddingTop: 40 }}>Loading…</div>;
    if (!poolId) return <div style={{ color: '#667', textAlign: 'center', paddingTop: 40 }}>Pick a pool to view its survivor contest.</div>;
    if (!data) return <div style={{ color: '#667', textAlign: 'center', paddingTop: 40 }}>Couldn't load this pool.</div>;

    if (!data.enabled) {
      return (
        <div style={s.card}>
          <div style={{ fontSize: 14, color: '#aab', marginBottom: 12 }}>
            Survivor isn't running in this pool yet. Each week you pick one team to win. Lose once and you're out, and you can't reuse a team. A tie keeps you alive, but the team is still used up. Last person standing takes it.
          </div>
          {data.isCommissioner
            ? <button style={{ ...s.btn, ...s.btnPrimary }} disabled={busy} onClick={() => enableSurvivor(true)}>Enable Survivor for this pool</button>
            : <div style={{ fontSize: 13, color: '#667' }}>Ask your commissioner to turn it on.</div>}
        </div>
      );
    }

    const { me, members, weekGames, currentWeek, winner, aliveCount } = data;
    const lockedOrPicked = !me.alive;

    return (
      <>
        {winner && (
          <div style={{ ...s.card, background: 'linear-gradient(135deg, rgba(255,215,0,0.12), rgba(213,10,10,0.08))', borderColor: 'rgba(255,215,0,0.4)' }}>
            <div style={{ fontSize: 20, fontWeight: 800, color: '#FFD700', fontFamily: "'Oswald',sans-serif" }}>🏆 {winner} is the last one standing!</div>
          </div>
        )}

        {/* Your pick */}
        <div style={s.card}>
          <div style={s.sectionTitle}>
            Your Pick {currentWeek ? `· ${currentWeek.label}` : ''} · {aliveCount} alive
          </div>
          {(data.myEntries || []).length > 1 && (
            <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
              {data.myEntries.map(e => {
                const on = e.entry_id === entryId;
                return (
                  <button key={e.entry_id} disabled={busy} onClick={() => switchLife(e.entry_id)}
                    style={{ ...s.btn, ...(on ? s.btnPrimary : s.btnMuted), opacity: e.alive ? 1 : 0.6 }}>
                    {e.username} {e.alive ? '· alive' : `· out ${e.eliminatedWeek || ''}`}
                  </button>
                );
              })}
            </div>
          )}
          {winner ? (
            <div style={{ color: '#FFD700', fontWeight: 700 }}>This season's Survivor is decided. No more picks.</div>
          ) : !me.alive ? (
            <div style={{ color: '#ff8888', fontWeight: 700 }}>🪦 You've been eliminated. Better luck next season.</div>
          ) : !currentWeek ? (
            <div style={{ color: '#667' }}>No active week right now.</div>
          ) : (
            <>
              <div style={{ fontSize: 13, color: '#889', marginBottom: 10 }}>
                {me.pick ? <>Current pick: <strong style={{ color: '#2ecc71' }}>{me.pick}</strong>. You can change it until that game kicks off.</> : 'Pick one team to win this week. Choose carefully, you can only use each team once.'}
              </div>
              {/* Every game, the Monday night one included. It was filtered out
                  here as "the tiebreaker game", so the two teams playing last
                  were never offered. */}
              {weekGames.map(g => {
                const homeUsed = me.usedTeams.includes(g.home_abbr) && me.pick !== g.home_abbr;
                const awayUsed = me.usedTeams.includes(g.away_abbr) && me.pick !== g.away_abbr;
                const renderTeam = (abbr, name, used) => {
                  const picked = me.pick === abbr;
                  const disabled = busy || g.locked || used;
                  let st = { ...s.teamBtn };
                  if (picked) st = { ...st, ...s.teamBtnPicked };
                  else if (used) st = { ...st, ...s.teamBtnUsed };
                  else if (g.locked) st = { ...st, ...s.teamBtnLocked };
                  return (
                    <button key={abbr} style={st} disabled={disabled}
                            title={used ? 'Already used this season' : g.locked ? 'Game already started' : name}
                            onClick={() => !disabled && pickTeam(abbr)}>
                      {abbr}{picked ? ' ✓' : ''}
                    </button>
                  );
                };
                return (
                  <div key={g.id} style={s.gameRow}>
                    {renderTeam(g.away_abbr, g.away_team, awayUsed)}
                    <span style={s.vs}>@</span>
                    {renderTeam(g.home_abbr, g.home_team, homeUsed)}
                  </div>
                );
              })}
            </>
          )}
        </div>

        {/* Standings */}
        <div style={s.card}>
          <div style={s.sectionTitle}>Survivors</div>
          {members.filter(m => m.picksMade > 0).length === 0 && (
            <div style={{ color: '#667', fontSize: 13 }}>No picks made yet.</div>
          )}
          {members.filter(m => m.picksMade > 0).map(m => (
            <div key={m.entry_id ?? m.user_id} style={s.memberRow}>
              <UserAvatar username={m.username} size={30} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: m.alive ? '#dde' : '#889' }}>
                  {m.username}{m.user_id === user.id && <span style={{ color: '#7fb3ff', fontSize: 11, marginLeft: 6 }}>(you)</span>}
                </div>
                <div style={{ marginTop: 3 }}>
                  {m.history.map((h, i) => (
                    <span key={i} style={{ ...s.histDot, ...outcomeStyle(h.outcome) }}
                          title={h.week_label + (h.team ? `: ${h.team}` : '')}>
                      {h.team || '•'}{h.outcome === 'win' ? ' ✓' : h.outcome === 'loss' ? ' ✗' : h.outcome === 'push' ? ' 🤝' : ''}
                    </span>
                  ))}
                </div>
              </div>
              <span style={m.alive ? s.aliveBadge : s.outBadge}>
                {m.alive ? 'Alive' : `Out · ${m.eliminatedWeek || ''}`}
              </span>
            </div>
          ))}
        </div>

        {data.isCommissioner && (
          <button style={{ ...s.btn, ...s.btnMuted }} disabled={busy} onClick={() => enableSurvivor(false)}>
            Disable Survivor
          </button>
        )}
      </>
    );
  };

  return (
    <div>
      <PoolSelector pools={pools} selectedPool={selectedPool} onSelect={onSelectPool} onManage={onManagePools} />
      <div style={s.page}>
        <div style={s.title}>💀 Survivor</div>
        <div style={s.sub}>One team a week. Lose and you're out. No team twice.</div>
        {body()}
      </div>
    </div>
  );
}
